import type { UiTagType } from "@shared/components/UiTag/index.vue";
import { t } from "@shared/utils/i18n";

export type ProductPairingViewMode = "product" | "box";

/** 产品配对 matchStatus：0 未配对，1 已配对 */
export const ProductPairingMatchStatus = {
    Unbound: 0,
    Bound: 1,
} as const;

/** 暂仅开放产品库存；按箱库存后续再开 */
export const PRODUCT_PAIRING_VIEW_TABS = [{ label: t("按产品库存配对"), value: "product" as const }];

export const PRODUCT_PAIRING_STATUS_OPTIONS = [
    { label: t("已配对"), value: ProductPairingMatchStatus.Bound },
    { label: t("未配对"), value: ProductPairingMatchStatus.Unbound },
];

export const PRODUCT_PAIRING_COMBINED_SEARCH_OPTIONS = [
    { label: t("海外仓SKU"), value: "warehouseSku" },
    { label: t("产品名称"), value: "productNameSnapshot", exact: false },
    { label: "1PSKU", value: "sku1p" },
    { label: t("品名"), value: "productName", exact: false },
    { label: "ASIN", value: "asin" },
    { label: "MSKU", value: "msku" },
];

/** 列表 Tab → 请求 inventoryType */
export const PRODUCT_PAIRING_INVENTORY_TYPE_BY_VIEW: Record<ProductPairingViewMode, string> = {
    product: "product",
    box: "box",
};

export interface ProductPairingGoodsItem {
    asin: string;
    msku: string;
    countryCode: string;
}

/** POST /manage/warehouseProductMatch/list → data.items[] */
export interface ProductPairingListAsinApi {
    asin?: string;
    msku?: string;
    sellerSku?: string;
    countryCode?: string;
    country_code?: string;
}

export interface ProductPairingListItemApi {
    corpId: string;
    corpName: string;
    externalWarehouseId: string;
    systemWarehouseName: string;
    warehouseSkuId: string;
    warehouseSku: string;
    productNameSnapshot: string;
    inventoryType: string;
    inventoryTypeLabel: string;
    matchId: string;
    matchQty: string;
    matchStatus: number;
    matchStatusLabel: string;
    productId: string;
    sku1p: string;
    productName: string;
    asins: ProductPairingListAsinApi[];
}

export interface ProductPairingRow {
    id: string;
    matchId: string;
    warehouseSkuId: string;
    corpId: string;
    productId: string;
    corpName: string;
    systemWarehouseName: string;
    warehouseSkuName: string;
    warehouseSku: string;
    pairedProductName: string;
    pairedSku1p: string;
    pairedProducts: ProductPairingGoodsItem[];
    pairingQty: string;
    inventoryType: string;
    isMapping: number;
    matchStatusLabel: string;
}

function mapAsinItem(item: ProductPairingListAsinApi): ProductPairingGoodsItem {
    return {
        asin: item.asin || "",
        msku: item.msku || item.sellerSku || "",
        countryCode: item.countryCode || item.country_code || "",
    };
}

export function mapProductPairingListItem(item: ProductPairingListItemApi): ProductPairingRow {
    return {
        id: item.warehouseSkuId,
        matchId: item.matchId,
        warehouseSkuId: item.warehouseSkuId,
        corpId: item.corpId,
        productId: item.productId,
        corpName: item.corpName || "",
        systemWarehouseName: item.systemWarehouseName || "",
        warehouseSkuName: item.productNameSnapshot || "",
        warehouseSku: item.warehouseSku || "",
        pairedProductName: item.productName || "",
        pairedSku1p: item.sku1p || "",
        pairedProducts: (item.asins ?? []).map(mapAsinItem).filter(g => g.asin || g.msku),
        pairingQty: item.matchQty || "--",
        inventoryType: item.inventoryType || "",
        isMapping: Number.isFinite(Number(item.matchStatus)) ? Number(item.matchStatus) : ProductPairingMatchStatus.Unbound,
        matchStatusLabel: item.matchStatusLabel || "",
    };
}

export function formatProductPairingQty(row: Pick<ProductPairingRow, "pairingQty" | "inventoryType">): string {
    const qty = row.pairingQty && row.pairingQty !== "--" ? row.pairingQty : "";
    if (!qty) return "--";
    const type = row.inventoryType || "";
    return type ? `${qty} ${type}` : qty;
}

export function getProductPairingStatusLabel(isMapping: number): string {
    if (isMapping === ProductPairingMatchStatus.Bound) return t("已配对");
    if (isMapping === ProductPairingMatchStatus.Unbound) return t("未配对");
    return "--";
}

export function getProductPairingStatusTagType(isMapping: number): UiTagType {
    return isMapping === ProductPairingMatchStatus.Bound ? "success" : "neutral";
}

/** 行内配对二次确认文案 */
export function buildProductPairingConfirmMessage(sku1p: string, warehouseSku: string): string {
    return t(`【${sku1p || "--"}】与【${warehouseSku || "--"}】进行配对?`);
}

/** 解除配对二次确认文案 */
export function buildProductUnpairConfirmMessage(): string {
    return t("确认解除该商品的配对关系?");
}
